import { EditOrigin, EditSessionOp } from '../../../shared/edit-session/edit-session';

/**
 * The Sales Route page's edit-session vocabulary: what a day looks like while it
 * is being edited, and the operations that move it.
 *
 * Kept out of the component on purpose. `reduceDay` is a pure fold the session
 * re-runs on every undo, and the cost estimate below is arithmetic — both are
 * worth testing without a TestBed, and neither needs Angular.
 *
 * ## The three ownership states, seen from the browser
 *
 * The server decides which of them an edit belongs to; this file only has to
 * express them so the page can SAY which one it is asking for:
 *
 *  - **Day only.** A stop added to or taken off this date. Permanent ownership
 *    is untouched — the badge says `today only`, and a removed row stays on
 *    screen struck through reading "still yours · back tomorrow".
 *  - **Permanent.** A cross-rep move, which an admin's Save performs outright.
 *  - **Requested.** The same cross-rep move asked for by a rep, which changes
 *    nothing until an admin decides it.
 *
 * The last two travel in the SAME `moves` field of the save. The page never
 * elects which it gets: `permanent` here is a *prediction* used for wording, and
 * the server re-decides it from the caller's grant. That is why a rep's transfer
 * control is offered rather than disabled — refusing in the UI would hide the
 * one path the design exists to provide.
 */

/** A door on one rep's day. */
export interface DayStop {
  /** Identity across a save: the server addresses stops by (type, id), not by
   *  the route-stop row id, which a stop added this session does not have. */
  readonly key: string;
  readonly stopId: number | null;
  readonly objectType: string;
  readonly objectId: number;
  readonly title: string | null;
  readonly address: string | null;
  readonly lat: number | null;
  readonly lng: number | null;
  readonly status: string;
  readonly outcome: string | null;
  readonly pinned: boolean;
  readonly pinReason: string | null;
  readonly reasons: readonly string[];
  /** Planner numbers, describing the SAVED order. Shown, never recomputed. */
  readonly plannedArrival: string | null;
  readonly driveSecondsFromPrevious: number | null;
  readonly driveMetersFromPrevious: number | null;
  readonly serviceMinutes: number | null;
  readonly visitedAt: string | null;
  readonly note: string | null;
  /** Where it sat in the last saved day; -1 when it was put here this session. */
  readonly savedIndex: number;
}

/** A stop taken off this date. Still owned by the rep — it comes back tomorrow. */
export interface DroppedStop {
  readonly stop: DayStop;
  readonly origin: EditOrigin;
}

/**
 * "This door should belong to that rep", pending a Save.
 *
 * @param permanent What the page EXPECTS the server to do, from the caller's
 *   own permission — used only to word the card. The server decides again.
 */
export interface ProposedTransfer {
  readonly stop: DayStop;
  readonly toUserId: number;
  readonly toUserName: string;
  readonly toTerritoryId: number | null;
  readonly reason: string | null;
  readonly permanent: boolean;
  readonly origin: EditOrigin;
}

export interface DayEndpoints { startAddress?: string | null; endAddress?: string | null; startLat: number | null; startLng: number | null; endLat: number | null; endLng: number | null; returnsToStart: boolean; }

export interface DayState {
  readonly endpoints?: DayEndpoints;
  /** In visit order. Position is the sequence: a stop's number is its index. */
  readonly stops: readonly DayStop[];
  /** Struck-through rows: off this date only, permanent ownership untouched. */
  readonly dropped: readonly DroppedStop[];
  readonly transfers: readonly ProposedTransfer[];
  /** Which doors this session has touched, and who touched them — the amber /
   *  purple convention needs the origin, not just the fact. */
  readonly touched: Readonly<Record<string, EditOrigin>>;
}

export const EMPTY_DAY: DayState = { stops: [], dropped: [], transfers: [], touched: {} };

interface OpBase {
  readonly label: string;
  readonly origin?: EditOrigin;
}

/**
 * One thing the page can do to a day. Plain data: the Copilot will propose these
 * without holding a reference to the component, and the yellow strip lists them
 * verbatim.
 */
export type DayOp =
  | (OpBase & { readonly kind: 'endpoints'; readonly endpoints: DayEndpoints })
  | (OpBase & { readonly kind: 'reorder'; readonly order: readonly string[] })
  | (OpBase & { readonly kind: 'add'; readonly stop: DayStop; readonly at: number | null })
  | (OpBase & { readonly kind: 'remove'; readonly key: string })
  | (OpBase & { readonly kind: 'restore'; readonly key: string })
  | (OpBase & {
      readonly kind: 'transfer';
      readonly key: string;
      readonly toUserId: number;
      readonly toUserName: string;
      readonly toTerritoryId: number | null;
      readonly reason: string | null;
      readonly permanent: boolean;
    })
  | (OpBase & { readonly kind: 'withdraw-transfer'; readonly key: string });

export function stopKey(objectType: string, objectId: number): string {
  return `${objectType}:${objectId}`;
}

/** Every `kind` a day operation can carry — the runtime half of `DayOp`, so the
 *  guard below cannot drift from the union as it grows. */
const DAY_OP_KINDS: readonly DayOp['kind'][] =
  ['endpoints', 'reorder', 'add', 'remove', 'restore', 'transfer', 'withdraw-transfer'];

/**
 * Is this one of THIS page's operations?
 *
 * The Copilot is not the routed component and reaches the session through
 * `CopilotEditTarget`, whose `propose` takes the type-erased `EditSessionOp` —
 * an operation is a page's own union and nothing outside the page can be typed
 * against it. This is the narrowing the page does on the way back in, so a
 * proposal the Sales Route page cannot express is refused rather than folded
 * into the day as an operation the reducer will silently ignore.
 */
export function isDayOp(op: EditSessionOp): op is DayOp {
  const kind = (op as Partial<DayOp>).kind;
  return typeof op?.label === 'string'
    && !!kind
    && DAY_OP_KINDS.includes(kind);
}

/**
 * The fold. Pure: returns a new state and never mutates its argument, because
 * the session replays it from the last saved baseline after every undo.
 */
export function reduceDay(state: DayState, op: DayOp): DayState {
  const origin: EditOrigin = op.origin ?? 'user';
  switch (op.kind) {
    case 'endpoints': return { ...state, endpoints: op.endpoints };
    case 'reorder':      return applyReorder(state, op.order, origin);
    case 'add':          return applyAdd(state, op.stop, op.at, origin);
    case 'remove':       return applyRemove(state, op.key, origin);
    case 'restore':      return applyRestore(state, op.key);
    case 'transfer':     return applyTransfer(state, op, origin);
    case 'withdraw-transfer': return applyWithdraw(state, op.key);
    default:             return state;
  }
}

function mark(
  touched: Readonly<Record<string, EditOrigin>>, key: string, origin: EditOrigin,
): Record<string, EditOrigin> {
  return { ...touched, [key]: origin };
}

function unmark(
  touched: Readonly<Record<string, EditOrigin>>, key: string,
): Record<string, EditOrigin> {
  const next = { ...touched };
  delete next[key];
  return next;
}

/**
 * Re-sequences by key. Keys the day does not hold are ignored and stops the
 * order does not name keep their relative position at the end, so a reorder
 * recorded before an unrelated undo can still be replayed against a day that no
 * longer matches it exactly.
 */
function applyReorder(
  state: DayState, order: readonly string[], origin: EditOrigin,
): DayState {
  const byKey = new Map(state.stops.map(s => [s.key, s]));
  const next: DayStop[] = [];
  for (const key of order) {
    const stop = byKey.get(key);
    if (stop) { next.push(stop); byKey.delete(key); }
  }
  for (const stop of state.stops) if (byKey.has(stop.key)) next.push(stop);

  let touched = state.touched;
  next.forEach((stop, index) => {
    if (state.stops[index]?.key !== stop.key) touched = mark(touched, stop.key, origin);
  });
  return { ...state, stops: next, touched };
}

function applyAdd(
  state: DayState, stop: DayStop, at: number | null, origin: EditOrigin,
): DayState {
  if (state.stops.some(s => s.key === stop.key)) return state;
  // Adding back something taken off today withdraws the removal rather than
  // stacking a second, contradictory change.
  const dropped = state.dropped.filter(d => d.stop.key !== stop.key);
  const next = [...state.stops];
  const index = at == null || at < 0 || at > next.length ? next.length : at;
  next.splice(index, 0, stop);
  return { ...state, stops: next, dropped, touched: mark(state.touched, stop.key, origin) };
}

function applyRemove(state: DayState, key: string, origin: EditOrigin): DayState {
  const stop = state.stops.find(s => s.key === key);
  if (!stop) return state;
  const stops = state.stops.filter(s => s.key !== key);
  // Something put on the day this session simply goes: it was never on the
  // saved day, so there is no ownership left behind to explain.
  if (stop.savedIndex < 0) {
    return { ...state, stops, touched: unmark(state.touched, key) };
  }
  return {
    ...state,
    stops,
    dropped: [...state.dropped, { stop, origin }],
    touched: mark(state.touched, key, origin),
  };
}

function applyRestore(state: DayState, key: string): DayState {
  const entry = state.dropped.find(d => d.stop.key === key);
  if (!entry) return state;
  const stops = [...state.stops];
  const index = Math.min(Math.max(entry.stop.savedIndex, 0), stops.length);
  stops.splice(index, 0, entry.stop);
  return {
    ...state,
    stops,
    dropped: state.dropped.filter(d => d.stop.key !== key),
    // Back where it was and still owned by the same rep: nothing is proposed
    // about this door any more.
    touched: unmark(state.touched, key),
  };
}

function applyTransfer(
  state: DayState,
  op: Extract<DayOp, { kind: 'transfer' }>,
  origin: EditOrigin,
): DayState {
  const stop = state.stops.find(s => s.key === op.key)
    ?? state.dropped.find(d => d.stop.key === op.key)?.stop
    ?? state.transfers.find(t => t.stop.key === op.key)?.stop;
  if (!stop) return state;

  const transfer: ProposedTransfer = {
    stop,
    toUserId: op.toUserId,
    toUserName: op.toUserName,
    toTerritoryId: op.toTerritoryId,
    reason: op.reason,
    permanent: op.permanent,
    origin,
  };
  const transfers = [...state.transfers.filter(t => t.stop.key !== op.key), transfer];

  // A permanent move takes the door off this rep's day: it is not theirs any
  // more. A REQUEST changes nothing until an admin decides it, so the stop
  // stays exactly where it is and is only badged.
  const stops = op.permanent ? state.stops.filter(s => s.key !== op.key) : state.stops;
  return { ...state, stops, transfers, touched: mark(state.touched, op.key, origin) };
}

function applyWithdraw(state: DayState, key: string): DayState {
  const transfer = state.transfers.find(t => t.stop.key === key);
  if (!transfer) return state;
  const transfers = state.transfers.filter(t => t.stop.key !== key);
  let stops = state.stops;
  if (transfer.permanent && !stops.some(s => s.key === key)
      && !state.dropped.some(d => d.stop.key === key)) {
    const next = [...stops];
    const index = Math.min(Math.max(transfer.stop.savedIndex, 0), next.length);
    next.splice(index, 0, transfer.stop);
    stops = next;
  }
  return { ...state, stops, transfers, touched: unmark(state.touched, key) };
}

// ── What a reorder costs ───────────────────────────────────────────────────

export interface LatLng { readonly lat: number; readonly lng: number; }

/**
 * What the estimate is measured against: where the day starts and finishes, and
 * how fast this particular day drives.
 */
export interface CostContext {
  readonly start: LatLng | null;
  readonly finish: LatLng | null;
  /** Derived from the plan's own totals, so a dense city day and a rural one do
   *  not get the same guess. */
  readonly metersPerSecond: number;
}

/**
 * Straight lines are shorter than streets. 1.3 is the usual planar detour
 * factor for a gridded city; it is a constant, not a measurement, which is
 * precisely why the number this file produces is labelled an estimate.
 */
export const ROAD_DETOUR_FACTOR = 1.3;
/** ~40 km/h, used only when the day carries no planner totals to learn from. */
export const DEFAULT_METERS_PER_SECOND = 11.1;

export function metersPerSecondFor(
  driveMeters: number | null | undefined, driveSeconds: number | null | undefined,
): number {
  if (!driveMeters || !driveSeconds) return DEFAULT_METERS_PER_SECOND;
  const mps = driveMeters / driveSeconds;
  // A plan with one very short leg can produce nonsense; clamp to walking pace
  // and motorway pace rather than dividing by something absurd.
  return Math.min(Math.max(mps, 3), 30);
}

export function haversineMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Located stops, plus the start and finish when the day has them. */
function path(stops: readonly DayStop[], ctx: CostContext): LatLng[] {
  const points: LatLng[] = [];
  if (ctx.start) points.push(ctx.start);
  for (const stop of stops) {
    if (stop.lat != null && stop.lng != null) points.push({ lat: stop.lat, lng: stop.lng });
  }
  if (ctx.finish) points.push(ctx.finish);
  return points;
}

export function estimateDriveSeconds(stops: readonly DayStop[], ctx: CostContext): number {
  const points = path(stops, ctx);
  let meters = 0;
  for (let i = 1; i < points.length; i++) meters += haversineMeters(points[i - 1], points[i]);
  return (meters * ROAD_DETOUR_FACTOR) / ctx.metersPerSecond;
}

export interface ReorderPreview {
  readonly key: string;
  readonly from: number;
  readonly to: number;
  readonly deltaSeconds: number;
  /** "4 → 5 · +4 min". */
  readonly label: string;
}

/**
 * What moving one stop to a new position would cost.
 *
 * **An estimate, not a route.** It is great-circle distance times a detour
 * constant, divided by the speed this day's own plan implies — the road graph
 * lives on the server and asking it per drag would put a request in a mousemove
 * handler. Both halves of the subtraction use the same estimator, so the bias
 * largely cancels and the SIGN is reliable even where the magnitude is soft.
 * The exact cost comes back from the planner on the next run.
 */
export function previewReorder(
  stops: readonly DayStop[], key: string, toIndex: number, ctx: CostContext,
): ReorderPreview | null {
  const from = stops.findIndex(s => s.key === key);
  if (from < 0) return null;
  const bounded = Math.min(Math.max(toIndex, 0), stops.length - 1);
  if (bounded === from) return null;

  const next = [...stops];
  const [moved] = next.splice(from, 1);
  next.splice(bounded, 0, moved);

  const delta = estimateDriveSeconds(next, ctx) - estimateDriveSeconds(stops, ctx);
  return {
    key,
    from: from + 1,
    to: bounded + 1,
    deltaSeconds: delta,
    label: `${from + 1} → ${bounded + 1} · ${costWords(delta)}`,
  };
}

/** "+4 min" / "−7 min" / "about the same". */
export function costWords(deltaSeconds: number): string {
  const minutes = Math.round(deltaSeconds / 60);
  if (minutes === 0) return 'about the same';
  return minutes > 0 ? `+${minutes} min` : `−${Math.abs(minutes)} min`;
}

/**
 * Where a stop dropped at this point belongs in the order: the leg it is
 * cheapest to splice into. Cheapest insertion is what a rep means by dropping a
 * pin between two others — the nearest pin alone cannot tell "before" from
 * "after".
 */
export function insertionIndexFor(
  stops: readonly DayStop[], key: string, at: LatLng, ctx: CostContext,
): number {
  // The day with the dragged stop lifted out - which is exactly the array a
  // reorder splices back into, so an index here is an index there.
  const others = stops.filter(s => s.key !== key);
  const located = others.filter(s => s.lat != null && s.lng != null);
  if (!located.length) return 0;

  const points = path(located, ctx);
  // path() prefixes the start point when the day has one; the offset maps a leg
  // index back onto a position among the located stops.
  const offset = ctx.start ? 1 : 0;
  let best = 0;
  let bestCost = Number.POSITIVE_INFINITY;
  for (let i = 1; i < points.length; i++) {
    const cost = haversineMeters(points[i - 1], at)
      + haversineMeters(at, points[i])
      - haversineMeters(points[i - 1], points[i]);
    if (cost < bestCost) { bestCost = cost; best = i - offset; }
  }
  // With no start point there is no leg INTO the first stop, and with no finish
  // none out of the last: the two ends are candidates in their own right, at
  // the plain cost of the one new leg they add.
  if (!ctx.start) {
    const cost = haversineMeters(at, points[0]);
    if (cost < bestCost) { bestCost = cost; best = 0; }
  }
  if (!ctx.finish) {
    const cost = haversineMeters(points[points.length - 1], at);
    if (cost < bestCost) { bestCost = cost; best = located.length; }
  }

  if (best >= located.length) return others.length;
  const target = located[Math.max(best, 0)];
  return Math.max(others.findIndex(s => s.key === target.key), 0);
}
